import { store } from '@/firebase'
import { FormulaUser } from '@/helpers/fs/formulaUser'
import {
  CollectionName,
  FSTeamMember,
  FSUserPublicProfile,
  OauthServiceName,
  WithId
} from '@WombatTechnology/nitte-time-lib'
import {
  collection,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  query,
  updateDoc,
  where
} from 'firebase/firestore'
import { toData, toDataWithId } from '../toData'
import { UserPublicProfile } from '../userPublicProfile'
import { AuthorizedService } from '../users/authorizedService'

export class TeamMember {
  constructor(
    readonly uid: string | undefined,
    readonly ref: DocumentReference<
      FSTeamMember<DocumentReference<FSUserPublicProfile>>
    >,
    readonly data: FSTeamMember<DocumentReference<FSUserPublicProfile>>,
    readonly profile: WithId<FSUserPublicProfile> | null,
    readonly authorizedServices: FormulaUser['authorizedServices']
  ) {}

  get id() {
    return this.ref.id
  }

  get email() {
    return this.data.email
  }

  get approved() {
    return this.data.approved
  }

  get formulaUser(): FormulaUser {
    return {
      id: this.profile!.id,
      icon: this.icon,
      name: this.name,
      email: this.email,
      type: 'user',
      authorizedServices: this.authorizedServices,
      // チームに所属している時点でチームプラン
      plan: 'team'
    }
  }

  get icon() {
    return this.profile?.icon
  }

  get name() {
    return this.profile?.name
  }

  get userId() {
    return this.profile?.userId
  }

  get isAdmin() {
    return this.data.isAdmin
  }

  get isPaymentAdmin() {
    return this.data.isPaymentAdmin
  }

  get isScopeAuthed() {
    return UserPublicProfile.isCalendarScopeAuthorized(this.profile)
  }

  // instance
  async approveInvitation() {
    return updateDoc(this.ref, {
      approved: true,
      userPublicProfileRef: UserPublicProfile.loginUserDoc()
    })
  }

  async delete() {
    return deleteDoc(this.ref)
  }

  async update(
    data: Partial<FSTeamMember<DocumentReference<FSUserPublicProfile>>>
  ) {
    return updateDoc(this.ref, data)
  }

  // static
  static async build(
    teamId: string,
    email: string,
    paymentAdminUid: string | null
  ) {
    const ref = this.doc(teamId, email)
    const data = await toData(ref)
    const uid = data.userPublicProfileRef?.id
    let profile = null
    if (data.userPublicProfileRef) {
      profile = await toDataWithId(data.userPublicProfileRef)
    }

    let authorizedServices: OauthServiceName[] = []
    if (uid) {
      authorizedServices = await AuthorizedService.authorizedServices(uid)
    }
    return new TeamMember(uid, ref, data, profile, authorizedServices)
  }

  static doc(teamId: string, email: string) {
    return doc<
      FSTeamMember<DocumentReference<FSUserPublicProfile>>,
      DocumentData
    >(this.collection(teamId), email)
  }

  static collection(teamId: string) {
    return collection(
      store,
      `${CollectionName.Teams}/${teamId}/${CollectionName.Members}`
    ) as CollectionReference<
      FSTeamMember<DocumentReference<FSUserPublicProfile>>
    >
  }

  static collectionGroup() {
    return collectionGroup(
      store,
      `${CollectionName.Members}`
    ) as CollectionReference<
      FSTeamMember<DocumentReference<FSUserPublicProfile>>
    >
  }

  static membersQuery(userEmail: string, approved: boolean) {
    return query(
      this.collectionGroup(),
      where('email', '==', userEmail),
      where('approved', '==', approved)
    )
  }
}
